import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { SideProjectCard } from '@/components/side-projects/SideProjectCard';
import { mockProjects, mockTimeEntries, mockTasks } from '@/mocks/data';
import { Link } from 'react-router-dom';

function SideProjectsContent() {
  const sideProjects = mockProjects.filter((project) => project.type === 'side-project');

  const projectsWithStats = sideProjects.map((project) => {
    const projectTimeEntries = mockTimeEntries.filter((entry) => entry.projectId === project.id);
    const totalHours = projectTimeEntries.reduce((acc, entry) => acc + entry.duration / 3600, 0);
    const activeTasks = mockTasks.filter(
      (task) => task.projectId === project.id && task.status !== 'completed'
    ).length;
    const completedTasks = mockTasks.filter(
      (task) => task.projectId === project.id && task.status === 'completed'
    ).length;

    return {
      ...project,
      totalHours: Math.round(totalHours * 10) / 10,
      activeTasks,
      completedTasks,
    };
  });

  const totalHours = projectsWithStats.reduce((acc, p) => acc + p.totalHours, 0);
  const totalActiveTasks = projectsWithStats.reduce((acc, p) => acc + p.activeTasks, 0);

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Breadcrumb items={[{ label: 'Side Projects' }]} />
          <h1 className="text-3xl font-bold text-gray-900">Side Projects</h1>
          <p className="text-gray-600 mt-1">Your personal projects and experiments</p>
        </div>

        <Link
          to="/side-projects/new"
          className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Side Project
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Projects</p>
          <p className="text-3xl font-bold text-gray-900">{sideProjects.length}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Hours</p>
          <p className="text-3xl font-bold text-flowky-primary">{totalHours.toFixed(1)}h</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Active Tasks</p>
          <p className="text-3xl font-bold text-gray-900">{totalActiveTasks}</p>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projectsWithStats.map((project) => (
          <SideProjectCard key={project.id} project={project} />
        ))}
      </div>

      {projectsWithStats.length === 0 && (
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg
            className="w-16 h-16 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No side projects yet</h3>
          <p className="text-gray-600 mb-4">Start tracking your personal projects</p>
          <Link
            to="/side-projects/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Your First Side Project
          </Link>
        </div>
      )}
    </AppLayout>
  );
}

export default function SideProjectsPage() {
  return (
    <ProtectedRoute>
      <SideProjectsContent />
    </ProtectedRoute>
  );
}
